/**
 * A single Color Sampler readout row fed into InfoPanel.update().
 * Structurally compatible with core/tools/ColorSamplerTool's ColorSampler
 * (that type has extra `id`/`enabled` fields, which is fine — this is a
 * narrower read-only view used purely for display).
 */
export interface ColorSamplerReadout {
  x: number;
  y: number;
  color: { r: number; g: number; b: number };
}

export class InfoPanel {
  private container: HTMLElement;
  private els: Record<string, HTMLElement> = {} as Record<string, HTMLElement>;

  constructor() {
    this.container = document.createElement("div");
    this.container.className = "info-panel";
    this.container.innerHTML = `
      <div class="panel-title">Info</div>
      <div class="info-section">
        <div class="info-section-title">Cursor</div>
        <div class="info-panel__row"><span class="muted">X:</span> <span class="info-val" id="info-x">—</span></div>
        <div class="info-panel__row"><span class="muted">Y:</span> <span class="info-val" id="info-y">—</span></div>
      </div>
      <div class="info-section">
        <div class="info-section-title">Color</div>
        <div class="info-panel__row"><span class="muted">RGB:</span> <span class="info-val" id="info-rgb">—</span></div>
        <div class="info-panel__row"><span class="muted">CMYK:</span> <span class="info-val" id="info-cmyk">—</span></div>
        <div class="info-panel__row"><span class="muted">Lab:</span> <span class="info-val" id="info-lab">—</span></div>
        <div class="info-panel__row"><span class="muted">Hex:</span> <span class="info-val" id="info-hex">—</span></div>
      </div>
      <div class="info-section" id="info-samplers-wrap" style="display:none;">
        <div class="info-section-title">Color Samplers</div>
        <div id="info-samplers-list"></div>
      </div>
      <div class="info-section">
        <div class="info-section-title">Selection</div>
        <div class="info-panel__row"><span class="muted">W:</span> <span class="info-val" id="info-sw">—</span></div>
        <div class="info-panel__row"><span class="muted">H:</span> <span class="info-val" id="info-sh">—</span></div>
      </div>
      <div class="info-section">
        <div class="info-section-title">Document</div>
        <div class="info-panel__row"><span class="muted">Size:</span> <span class="info-val" id="info-docsize">—</span></div>
        <div class="info-panel__row"><span class="muted">Res:</span> <span class="info-val" id="info-res">—</span></div>
        <div class="info-panel__row"><span class="muted">Mode:</span> <span class="info-val" id="info-mode">—</span></div>
      </div>
    `;

    this.els = {
      "info-x": this.container.querySelector("#info-x") as HTMLElement,
      "info-y": this.container.querySelector("#info-y") as HTMLElement,
      "info-rgb": this.container.querySelector("#info-rgb") as HTMLElement,
      "info-cmyk": this.container.querySelector("#info-cmyk") as HTMLElement,
      "info-lab": this.container.querySelector("#info-lab") as HTMLElement,
      "info-hex": this.container.querySelector("#info-hex") as HTMLElement,
      "info-samplers-wrap": this.container.querySelector("#info-samplers-wrap") as HTMLElement,
      "info-samplers-list": this.container.querySelector("#info-samplers-list") as HTMLElement,
      "info-sw": this.container.querySelector("#info-sw") as HTMLElement,
      "info-sh": this.container.querySelector("#info-sh") as HTMLElement,
      "info-docsize": this.container.querySelector("#info-docsize") as HTMLElement,
      "info-res": this.container.querySelector("#info-res") as HTMLElement,
      "info-mode": this.container.querySelector("#info-mode") as HTMLElement,
    };
  }

  mount(parent: HTMLElement): void {
    parent.appendChild(this.container);
  }

  update(data: {
    cursorPos?: { x: number; y: number } | null;
    cursorColor?: { r: number; g: number; b: number } | null;
    /**
     * Live readout list for the Color Sampler tool (Photoshop's Info panel
     * shows one swatch+RGB row per placed sampler, #1..#N). Pass the active
     * ColorSamplerTool's `samplers` array here; pass an empty array to clear
     * the section, or omit the key entirely to leave it untouched.
     */
    colorSamplers?: ColorSamplerReadout[] | null;
    selection?: { w: number; h: number } | null;
    document?: { width: number; height: number; dpi: number; mode: string } | null;
  }): void {
    if (data.cursorPos) {
      this.els["info-x"]!.textContent = String(Math.round(data.cursorPos.x));
      this.els["info-y"]!.textContent = String(Math.round(data.cursorPos.y));
    }

    if (data.cursorColor) {
      const { r, g, b } = data.cursorColor;
      this.els["info-rgb"]!.textContent = `${r}, ${g}, ${b}`;
      this.els["info-hex"]!.textContent = `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;

      // RGB to CMYK
      const cmyk = this._rgbToCmyk(r, g, b);
      this.els["info-cmyk"]!.textContent = `${cmyk.c}%, ${cmyk.m}%, ${cmyk.y}%, ${cmyk.k}%`;

      // RGB to Lab
      const lab = this._rgbToLab(r, g, b);
      this.els["info-lab"]!.textContent = `${lab.l.toFixed(1)}, ${lab.a.toFixed(1)}, ${lab.b.toFixed(1)}`;
    }

    if (data.colorSamplers !== undefined) {
      this._renderSamplers(data.colorSamplers);
    }

    if (data.selection) {
      this.els["info-sw"]!.textContent = `${Math.round(data.selection.w)} px`;
      this.els["info-sh"]!.textContent = `${Math.round(data.selection.h)} px`;
    }

    if (data.document) {
      this.els["info-docsize"]!.textContent = `${data.document.width} × ${data.document.height}`;
      this.els["info-res"]!.textContent = `${data.document.dpi} dpi`;
      this.els["info-mode"]!.textContent = data.document.mode;
    }
  }

  /**
   * Render the per-Color-Sampler readout list (index + swatch + RGB),
   * matching Photoshop's Info panel behavior of showing one row per
   * placed sampler (#1 through #N). Hides the section entirely when
   * there are no samplers.
   */
  private _renderSamplers(samplers: ColorSamplerReadout[] | null | undefined): void {
    const wrap = this.els["info-samplers-wrap"];
    const list = this.els["info-samplers-list"];
    if (!wrap || !list) return;

    if (!samplers || samplers.length === 0) {
      wrap.style.display = "none";
      list.innerHTML = "";
      return;
    }

    wrap.style.display = "";
    list.innerHTML = samplers.map((s, i) => {
      const { r, g, b } = s.color;
      const hex = `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
      return `
        <div class="info-panel__row info-sampler-row" data-sampler-index="${i}">
          <span class="info-sampler-swatch" style="display:inline-block;width:10px;height:10px;margin-right:4px;border:1px solid rgba(128,128,128,0.6);background-color:${hex};"></span>
          <span class="muted">#${i + 1}:</span>
          <span class="info-val">${r}, ${g}, ${b}</span>
        </div>
      `;
    }).join("");
  }

  private _rgbToCmyk(r: number, g: number, b: number): { c: number; m: number; y: number; k: number } {
    const rn = r / 255, gn = g / 255, bn = b / 255;
    const k = 1 - Math.max(rn, gn, bn);
    if (k === 1) return { c: 0, m: 0, y: 0, k: 100 };
    const c = (1 - rn - k) / (1 - k);
    const m = (1 - gn - k) / (1 - k);
    const y = (1 - bn - k) / (1 - k);
    return {
      c: Math.round(c * 100),
      m: Math.round(m * 100),
      y: Math.round(y * 100),
      k: Math.round(k * 100),
    };
  }

  private _rgbToLab(r: number, g: number, b: number): { l: number; a: number; b: number } {
    // RGB to XYZ
    let rn = r / 255, gn = g / 255, bn = b / 255;
    rn = rn > 0.04045 ? Math.pow((rn + 0.055) / 1.055, 2.4) : rn / 12.92;
    gn = gn > 0.04045 ? Math.pow((gn + 0.055) / 1.055, 2.4) : gn / 12.92;
    bn = bn > 0.04045 ? Math.pow((bn + 0.055) / 1.055, 2.4) : bn / 12.92;
    const x = (rn * 0.4124564 + gn * 0.3575761 + bn * 0.1804375) / 0.95047;
    const y = (rn * 0.2126729 + gn * 0.7151522 + bn * 0.0721750) / 1.00000;
    const z = (rn * 0.0193339 + gn * 0.1191920 + bn * 0.9503041) / 1.08883;

    const f = (t: number) => t > 0.008856 ? Math.cbrt(t) : (7.787 * t) + 16 / 116;
    const fx = f(x), fy = f(y), fz = f(z);

    return {
      l: Math.max(0, (116 * fy) - 16),
      a: 500 * (fx - fy),
      b: 200 * (fy - fz),
    };
  }

  destroy(): void {
    this.container.remove();
  }
}
